import { useSearchParams } from 'react-router-dom';
import { getInitialState, initInputState } from 'src/modules/user-input/InitInputState.ts';
import { UserInput } from 'src/modules/user-input/types.ts';
import { useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { targetUnits, targetUnitsAm } from 'src/modules/user-input/targetUnits.ts';
import { ORDER_BY_NUMBER_QUERY_PARAM, ORDER_BY_STRING_QUERY_PARAM } from 'src/modules/common/constants.ts';
import { FilterConfig } from 'src/utils/composeFilter.ts';
import { LocationStateContextType, initLocationState } from 'src/modules/locationStateContext.ts';
import { useLocationState } from 'src/modules/user-input/useLocationState.ts';

export function useUserInputState(config: FilterConfig): {
    state: UserInput;
    searchParams: URLSearchParams;
    setState: (newState: UserInput) => void;
    initialState: UserInput;
    locationState: LocationStateContextType['locationState'];
    setLocationState: (s: LocationStateContextType['locationState']) => void;
} {
    const { userSettings } = useContext(ConfigContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [locationState, setLocationState] = useLocationState();

    const initialState = getInitialState(userSettings.currentRegion === RegionsEnum.Am ? targetUnitsAm : targetUnits);

    const state = useMemo(() => initInputState(
        searchParams,
        initialState,
    ), [initialState, searchParams]);

    const setState = useCallback((newState: UserInput) => {
        setLocationState({ ...initLocationState });
        searchParams.set('input', JSON.stringify(newState));
        searchParams.delete(ORDER_BY_NUMBER_QUERY_PARAM);
        searchParams.delete(ORDER_BY_STRING_QUERY_PARAM);
        config.forEach((item) => {
            const [name] = item;
            searchParams.delete(name);
        });
        setSearchParams(searchParams);
    }, [config, searchParams, setLocationState, setSearchParams]);

    return { state, searchParams, setState, initialState, locationState, setLocationState };
}
